/**
 * CAP-40007 TCL-22 / TCL-SECURITY - Role-Based Access Control (RBAC) for the TCL Back Office.
 * Single source of truth for IDP role strings, hierarchy, areas and the RBAC policy table.
 *
 * Design: IDP role strings (IdpRoles.*) are used DIRECTLY as keys in RolePolicy and ROLE_PRIORITY.
 * There is no internal-role mapping layer – the values coming from the JWT are matched straight
 * against IdpRoles constants.  This means there is only one place to update when role names change.
 *
 * Display priority order (highest → lowest – see ROLE_PRIORITY and AuthorizationService):
 *   1. ADMIN      – full access to all areas, can modify protected content.
 *   2. OPERATOR   – Saisie area, full CRUD.
 *   3. EXTRACTION – Extraction area only.
 *   4. MULTIVIN   – Saisie area, read-only.
 *
 * Multi-role resolution:
 *   - ADMIN is exclusive and always grants the ADMIN policy.
 *   - Non-admin roles combine visible and writable areas.
 *   - Example: OPERATOR + EXTRACTION + MULTIVIN → Saisie and Extraction, with Saisie writable.
 *   - Example: EXTRACTION + MULTIVIN → Saisie read-only and Extraction writable.
 */

/**
 * TCL-SECURITY - IDP role strings EXACTLY as received in the JWT "roles" claim from PingFederate.
 * These are the single canonical role identifiers used throughout the application –
 * in RolePolicy keys, ROLE_PRIORITY and frontend guards.
 */
export const IdpRoles = {
  ADMIN:      'ZTCL.AD.ADMIN',
  EXTRACTION: 'ZTCL.AD.EXTRACTION',
  OPERATOR:   'ZTCL.AD.OPERATOR',
  MULTIVIN:   'ZTCL.AD.MULTIVIN'
};

/**
 * TCL-SECURITY - Display priority used to select the primary role when a user holds several roles
 * (index 0 = highest priority). Permission checks do not use this ordering.
 * Used by getCurrentRole() for display and diagnostics. Authorization decisions aggregate policies
 * in AuthorizationService, with ADMIN as the exclusive exception.
 */
export const ROLE_PRIORITY: string[] = [
  IdpRoles.ADMIN,
  IdpRoles.OPERATOR,
  IdpRoles.EXTRACTION,
  IdpRoles.MULTIVIN
];

// CAP-40007 TCL-22 - Functional areas, one per top-menu entry.
export enum TclArea {
  ADMIN = 'ADMIN',
  SAISIE = 'SAISIE',
  EXTRACTION = 'EXTRACTION'
}

// CAP-40007 TCL-22 - Central RBAC policy: which areas each role can see and write.
// Frontend policy table used by AuthorizationService.
export interface RolePolicyEntry {
  areas: TclArea[];
  writableAreas: TclArea[];
  canModifyProtectedContent: boolean;
}

export const RolePolicy: { [role: string]: RolePolicyEntry } = {
  // ZTCL.AD.ADMIN - Full access to all three menus; can read, write and modify protected content.
  [IdpRoles.ADMIN]: {
    areas: [TclArea.ADMIN, TclArea.SAISIE, TclArea.EXTRACTION],
    writableAreas: [TclArea.ADMIN, TclArea.SAISIE, TclArea.EXTRACTION],
    canModifyProtectedContent: true
  },
  // ZTCL.AD.MULTIVIN - Saisie menu only, strict read-only (no write, no title/header edits).
  [IdpRoles.MULTIVIN]: {
    areas: [TclArea.SAISIE],
    writableAreas: [],
    canModifyProtectedContent: false
  },
  // ZTCL.AD.EXTRACTION - Extraction menu only; can read and write in extraction area.
  [IdpRoles.EXTRACTION]: {
    areas: [TclArea.EXTRACTION],
    writableAreas: [TclArea.EXTRACTION],
    canModifyProtectedContent: false
  },
  // ZTCL.AD.OPERATOR - Saisie menu only; can update items/pages but NOT titles, headers or text boxes.
  [IdpRoles.OPERATOR]: {
    areas: [TclArea.SAISIE],
    writableAreas: [TclArea.SAISIE],
    canModifyProtectedContent: false
  }
};

// CAP-40007 TCL-22 - Topic numbers whose text/content is protected (e.g. "1.2.1 Performance").
export const PROTECTED_TOPIC_NUMBERS: string[] = ['1.2.1'];
